import { WorkspaceBranding } from "./workspaceMemberships"

export const BRAND_PRIMARY_COLOR = "#EFFF00"

// Self-service branding is only for a workspace's own admin, and never for
// "app" - it keeps Amalibri's reference look and stays staff-only (also
// enforced server-side, see back/src/api/workspace/controllers/workspace.ts).
export function canEditBranding(
  current: WorkspaceBranding | undefined,
  isAdminHere: boolean
): boolean {
  return Boolean(isAdminHere && current && current.subdomain !== "app")
}

const HEX_COLOR_RE = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/

// Mirrors the regex the backend enforces on primary_color.
export function isValidPrimaryColor(value: string): boolean {
  return HEX_COLOR_RE.test(value)
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex
  const [, r, g, b] = /^#([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})([A-Fa-f0-9]{2})$/.exec(
    normalized
  ) as RegExpExecArray
  return { r: parseInt(r, 16), g: parseInt(g, 16), b: parseInt(b, 16) }
}

// Standard WCAG relative luminance formula.
function getRelativeLuminance({
  r,
  g,
  b,
}: {
  r: number
  g: number
  b: number
}): number {
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

// Picks a readable black/white foreground for text and icons placed on top
// of a given background color, based on its WCAG relative luminance.
export function getContrastingTextColor(hex: string): "#000000" | "#FFFFFF" {
  if (!isValidPrimaryColor(hex)) return "#000000"
  return getRelativeLuminance(hexToRgb(hex)) > 0.5 ? "#000000" : "#FFFFFF"
}

export function workspaceAvatarColors(workspace: WorkspaceBranding): {
  bg: string
  color: "#000000" | "#FFFFFF"
} {
  const bg =
    workspace.primary_color && isValidPrimaryColor(workspace.primary_color)
      ? workspace.primary_color
      : BRAND_PRIMARY_COLOR

  return { bg, color: getContrastingTextColor(bg) }
}

export async function fetchCurrentWorkspaceBranding(
  subdomain: unknown
): Promise<WorkspaceBranding | null> {
  if (typeof subdomain !== "string" || !subdomain) {
    return null
  }

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/workspaces/current`,
      { headers: { "X-Workspace-Subdomain": subdomain } }
    )

    if (!response.ok) {
      return null
    }

    return (await response.json()) as WorkspaceBranding
  } catch {
    return null
  }
}
